import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';

type CrumbActions = {
  goToListing: () => void;
  goToResources: () => void;
};

type UserAccessBreadcrumbContextValue = {
  userName: string | null;
  assignmentsOpen: boolean;
  setUserName: (userName: string | null) => void;
  setAssignmentsOpen: (assignmentsOpen: boolean) => void;
  actions: React.MutableRefObject<CrumbActions>;
};

const noop = () => undefined;

const fallbackActions = {
  current: {
    goToListing: noop,
    goToResources: noop
  }
};

const fallbackValue: UserAccessBreadcrumbContextValue = {
  userName: null,
  assignmentsOpen: false,
  setUserName: noop,
  setAssignmentsOpen: noop,
  actions: fallbackActions
};

const UserAccessBreadcrumbContext =
  createContext<UserAccessBreadcrumbContextValue>(fallbackValue);

export const UserAccessBreadcrumbProvider = ({ children }) => {
  const [userName, setUserName] = useState<string | null>(null);
  const [assignmentsOpen, setAssignmentsOpen] = useState(false);
  const actions = useRef<CrumbActions>({
    goToListing: noop,
    goToResources: noop
  });
  const value = useMemo(
    () => ({
      userName,
      assignmentsOpen,
      setUserName,
      setAssignmentsOpen,
      actions
    }),
    [userName, assignmentsOpen]
  );

  return (
    <UserAccessBreadcrumbContext.Provider value={value}>
      {children}
    </UserAccessBreadcrumbContext.Provider>
  );
};

export const useUserAccessBreadcrumb = () =>
  useContext(UserAccessBreadcrumbContext);
